<script setup lang="ts">
/**
 * Auth error page wrapper used by most OAuth-callback error routes.
 *
 * Accepts a `code` prop, resolves `title`, `explanation`, and `actions` from
 * `ERROR_USER_MESSAGES[code]` (falling back to `DEFAULT_AUTH_ERROR_ACTIONS`
 * when the entry has no custom action list), then delegates rendering to
 * `ErrorView`. Adds a "Go To Login" button in the default slot that calls
 * `redirectToLogin()` to restart the OAuth flow.
 *
 * Used by thin route-level components (e.g. `InvalidStateInAuthResponseError401`)
 * that only supply the error code and rely on this wrapper for the full page.
 */
import { computed } from 'vue'
import ErrorView from '@/components/errors/ErrorView.vue'
import { redirectToLogin } from '@/api/keycloak_api'
import { ErrorCode } from '@/errors/codes'
import { DEFAULT_AUTH_ERROR_ACTIONS, ERROR_USER_MESSAGES } from '@/errors/error_user_messages'

const props = defineProps<{
  code: ErrorCode
}>()

const messages = computed(() => ERROR_USER_MESSAGES[props.code])
const actions = computed(() => messages.value.actions ?? DEFAULT_AUTH_ERROR_ACTIONS)

function goToLogin() {
  redirectToLogin()
}
</script>

<template>
  <ErrorView
    :code="props.code"
    :title="messages.title"
    :explanation="messages.explanation"
    :actions="actions"
  >
    <button class="tufte-button" @click="goToLogin">Go To Login</button>
  </ErrorView>
</template>
